import {
  CSSProperties,
  FC,
  Fragment,
  MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import cn from 'classnames'
import { Checkbox } from 'antd'
import { DeleteOutlined, EditOutlined } from '@ant-design/icons'
import type { CrmLeadTask } from '@/api/types/crm/crmLeadTasks.types'
import { useAddAlert } from '@/hooks/useAddAlert'
import { taskTrackerApi } from '@pages/Admin/TaskTrackerCRM/api/taskTracker.api'
import DeleteTaskModal from '@pages/Admin/TaskTrackerCRM/components/TaskTrackerTaskDrawer/DeleteTaskModal/DeleteTaskModal'
import TaskTrackerRichTextEditor from '@pages/Admin/TaskTrackerCRM/components/TaskTrackerRichTextEditor/TaskTrackerRichTextEditor'
import { isEmptyHtml } from '@pages/Admin/TaskTrackerCRM/components/TaskTrackerRichTextEditor/TaskTrackerRichTextEditor.shared'
import {
  formatLeadTaskDeadline,
  getLeadTaskPerformerLabel,
  isLeadTaskOverdue,
  buildLeadTaskStatusUpdatePayload,
} from './leadTasks.utils'
import classes from './index.module.css'

const EditIcon = EditOutlined as unknown as FC<{ className?: string }>
const DeleteIcon = DeleteOutlined as unknown as FC<{ className?: string }>

const DEFAULT_DESC_HEIGHT_PX = 24
const MIN_DESC_HEIGHT_PX = 22
const MAX_DESC_HEIGHT_PX = 420

const getErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error && error.message.trim()) {
    return error.message
  }
  return fallback
}

type LeadTasksTableProps = {
  tasks: CrmLeadTask[]
  onEdit: (taskId: number) => void
  onChanged: () => void
}

const LeadTasksTable: FC<LeadTasksTableProps> = ({
  tasks,
  onEdit,
  onChanged,
}) => {
  const addAlert = useAddAlert()
  const [busyTaskId, setBusyTaskId] = useState<number | null>(null)
  const [taskToDelete, setTaskToDelete] = useState<CrmLeadTask | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [rowHeights, setRowHeights] = useState<Record<number, number>>({})
  const [resizingTaskId, setResizingTaskId] = useState<number | null>(null)
  const resizeSessionRef = useRef<{
    taskId: number
    startY: number
    startHeight: number
  } | null>(null)

  const handleToggleCompleted = useCallback(
    async (task: CrmLeadTask, nextCompleted: boolean) => {
      if (busyTaskId != null || nextCompleted === task.is_completed) return

      setBusyTaskId(task.id)

      try {
        await taskTrackerApi.updateTask(
          task.id,
          buildLeadTaskStatusUpdatePayload(task, nextCompleted),
        )
        onChanged()
      } catch (error: unknown) {
        addAlert({
          type: 'error',
          title: 'Ошибка',
          text: getErrorMessage(error, 'Не удалось изменить статус задачи'),
        })
      } finally {
        setBusyTaskId(null)
      }
    },
    [addAlert, busyTaskId, onChanged],
  )

  const handleConfirmDelete = useCallback(async () => {
    if (!taskToDelete || deleting) return

    setDeleting(true)
    setBusyTaskId(taskToDelete.id)

    try {
      const result = await taskTrackerApi.deleteTask(taskToDelete.id)
      if (!result.deleted) {
        throw new Error('Не удалось удалить задачу')
      }
      setTaskToDelete(null)
      addAlert({ title: 'Задача удалена' })
      onChanged()
    } catch (error: unknown) {
      addAlert({
        type: 'error',
        title: 'Ошибка',
        text: getErrorMessage(error, 'Не удалось удалить задачу'),
      })
    } finally {
      setDeleting(false)
      setBusyTaskId(null)
    }
  }, [addAlert, deleting, onChanged, taskToDelete])

  const handleResizeStart = useCallback(
    (taskId: number, event: ReactMouseEvent<HTMLButtonElement>) => {
      event.preventDefault()
      event.stopPropagation()

      resizeSessionRef.current = {
        taskId,
        startY: event.clientY,
        startHeight: rowHeights[taskId] ?? DEFAULT_DESC_HEIGHT_PX,
      }
      setResizingTaskId(taskId)
    },
    [rowHeights],
  )

  useEffect(() => {
    if (resizingTaskId == null) return

    const handleMouseMove = (event: MouseEvent) => {
      const session = resizeSessionRef.current
      if (!session) return

      const nextHeight = Math.min(
        MAX_DESC_HEIGHT_PX,
        Math.max(
          MIN_DESC_HEIGHT_PX,
          session.startHeight + (event.clientY - session.startY),
        ),
      )

      setRowHeights((prev) => {
        if (prev[session.taskId] === nextHeight) return prev
        return { ...prev, [session.taskId]: nextHeight }
      })
    }

    const handleMouseUp = () => {
      resizeSessionRef.current = null
      setResizingTaskId(null)
    }

    document.body.style.cursor = 'ns-resize'
    document.body.style.userSelect = 'none'
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [resizingTaskId])

  return (
    <>
      <div className={classes.tableWrap}>
        <table className={classes.table}>
          <thead>
            <tr>
              <th className={cn(classes.tableHeadCell, classes.colDone)}>
                Выполнено
              </th>
              <th className={cn(classes.tableHeadCell, classes.colDeadline)}>
                Дедлайн
              </th>
              <th className={cn(classes.tableHeadCell, classes.colTitle)}>
                Задача
              </th>
              <th className={cn(classes.tableHeadCell, classes.colDescription)}>
                Описание
              </th>
              <th className={cn(classes.tableHeadCell, classes.colPerformer)}>
                Ответственный
              </th>
              <th
                className={cn(
                  classes.tableHeadCell,
                  classes.tableHeadCellLast,
                  classes.colActions,
                )}
                aria-label="Действия"
              />
            </tr>
          </thead>
          <tbody>
            {tasks.map((task) => {
              const deadlineLabel = formatLeadTaskDeadline(task.deadline)
              const performerLabel = getLeadTaskPerformerLabel(task)
              const overdue = isLeadTaskOverdue(task)
              const description = task.description?.trim() ?? ''
              const hasDescription = !isEmptyHtml(description)
              const isBusy = busyTaskId === task.id
              const descHeight = rowHeights[task.id]
              const descriptionCellStyle =
                descHeight != null
                  ? ({
                      '--lead-task-desc-height': `${descHeight}px`,
                    } as CSSProperties)
                  : undefined

              const handleEditClick = (
                event: ReactMouseEvent<HTMLButtonElement>,
              ) => {
                event.stopPropagation()
                onEdit(task.id)
              }

              const handleDeleteClick = (
                event: ReactMouseEvent<HTMLButtonElement>,
              ) => {
                event.stopPropagation()
                setTaskToDelete(task)
              }

              return (
                <Fragment key={task.id}>
                  <tr
                    className={cn(
                      classes.row,
                      task.is_completed && classes.rowCompleted,
                    )}
                  >
                    <td
                      className={cn(
                        classes.tableCell,
                        classes.tableCellFirst,
                        classes.colDone,
                      )}
                    >
                      <div className={classes.colDoneCell}>
                        <Checkbox
                          checked={task.is_completed}
                          disabled={isBusy}
                          onChange={(event) => {
                            void handleToggleCompleted(
                              task,
                              event.target.checked,
                            )
                          }}
                          aria-label={
                            task.is_completed
                              ? 'Отметить как невыполненную'
                              : 'Отметить как выполненную'
                          }
                        />
                      </div>
                    </td>
                    <td className={cn(classes.tableCell, classes.colDeadline)}>
                      <span
                        className={cn(
                          classes.deadline,
                          overdue && classes.deadlineOverdue,
                        )}
                      >
                        {deadlineLabel || '—'}
                        {overdue ? (
                          <span className={classes.deadlineOverdueMark}>
                            {' '}
                            · просрочен
                          </span>
                        ) : null}
                      </span>
                    </td>
                    <td className={cn(classes.tableCell, classes.colTitle)}>
                      <span className={classes.title}>
                        {task.title?.trim() || 'Без названия'}
                      </span>
                    </td>
                    <td
                      className={cn(classes.tableCell, classes.colDescription)}
                    >
                      <div
                        className={classes.descriptionCell}
                        style={descriptionCellStyle}
                      >
                        {hasDescription ? (
                          <div className={classes.descriptionEditor}>
                            <TaskTrackerRichTextEditor
                              value={description}
                              disabled
                              compact
                            />
                          </div>
                        ) : (
                          <span className={classes.descriptionEmpty}>—</span>
                        )}
                      </div>
                    </td>
                    <td className={cn(classes.tableCell, classes.colPerformer)}>
                      <span className={classes.performer}>
                        {performerLabel || '—'}
                      </span>
                    </td>
                    <td
                      className={cn(
                        classes.tableCell,
                        classes.tableCellLast,
                        classes.colActions,
                      )}
                    >
                      <div className={classes.actions}>
                        <button
                          type="button"
                          className={classes.actionButton}
                          aria-label="Открыть задачу"
                          title="Открыть задачу"
                          disabled={isBusy}
                          onClick={handleEditClick}
                        >
                          <EditIcon className={classes.actionIcon} />
                        </button>
                        <button
                          type="button"
                          className={cn(
                            classes.actionButton,
                            classes.actionButtonDanger,
                          )}
                          aria-label="Удалить задачу"
                          title="Удалить задачу"
                          disabled={isBusy}
                          onClick={handleDeleteClick}
                        >
                          <DeleteIcon className={classes.actionIcon} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  <tr className={classes.rowResize}>
                    <td className={classes.rowResizeCell} colSpan={6}>
                      <button
                        type="button"
                        className={cn(classes.rowResizeHandle, {
                          [classes.rowResizeHandleActive]:
                            resizingTaskId === task.id,
                        })}
                        aria-label="Изменить высоту строки"
                        title="Потяните, чтобы изменить высоту"
                        onMouseDown={(event) =>
                          handleResizeStart(task.id, event)
                        }
                      />
                    </td>
                  </tr>
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>

      <DeleteTaskModal
        taskTitle={taskToDelete?.title?.trim() || 'Без названия'}
        open={taskToDelete != null}
        loading={deleting}
        onConfirm={() => void handleConfirmDelete()}
        onDismiss={() => {
          if (deleting) return
          setTaskToDelete(null)
        }}
      />
    </>
  )
}

export default LeadTasksTable
